// Minimal, rule-first Restaurant Finder (from scratch). Keeps same input/output shapes.
import { searchPlacesServer, ServerPlace, getPlaceDetailsServer } from './placesSearch';
import { fetchBasicMeta } from './oembed';
import { geocodeAddress } from '../lib/maps/placesClient';

export type FindInput = { mediaUrl?: string; caption?: string; hashtags?: string[]; cityHint?: string; file?: File };
export type FindOutput = Array<{ 
  placeId: string; 
  name: string; 
  address: string; 
  confidence: number; 
  reasons: string[]; 
  rating?: number; 
  website?: string; 
  url?: string; 
  phone?: string;
  cuisine?: string;
  openNow?: boolean;
  hours?: string[];
  priceLevel?: number;
}>;

function stripDiacritics(s: string) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
function normalizeName(s: string) {
  return stripDiacritics(s || '')
    .toLowerCase()
    .replace(/[@#]/g, ' ')
    .replace(/[^a-z0-9'&\- ]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const STOP_WORDS = new Set([
  'restaurante','restaurant','food','comida','tacos','pozole','birria','pizza','burger','bar','cafe','cafeteria','panaderia','bakery','sushi','ramen','pho','pasta','bbq','steak',
  'video','reel','tiktok','youtube','instagram','shorts','vlog','guide','tour','review','receta','recipe','street','market','mercado','foodcourt','food-court',
]);

function extractCandidatesFromText(text: string): string[] {
  const out = new Set<string>();
  const raw = text || '';
  // quoted phrases
  const rxQuoted = /["“”']([^"“”']{3,})["“”']/g;
  let m: RegExpExecArray | null;
  while ((m = rxQuoted.exec(raw))) {
    const n = normalizeName(m[1]);
    if (!n) continue;
    const tokens = n.split(' ');
    if (tokens.length >= 2 && tokens.every(t => !STOP_WORDS.has(t))) out.add(n);
  }
  // after prepositions: en|at|en el|en la|in
  const rxAt = /\b(?:en\s+(?:el|la|los|las)?|at|in)\s+([A-ZÁÉÍÓÚÑ][A-Za-zÁÉÍÓÚÑáéíóúñ0-9'&\- ]{2,})/g;
  while ((m = rxAt.exec(raw))) {
    const n = normalizeName(m[1]);
    if (!n) continue;
    const tokens = n.split(' ');
    if (tokens.length >= 2 && tokens.every(t => !STOP_WORDS.has(t))) out.add(n);
  }
  // TitleCase sequences: 2-5 words starting with capital letters (rough heuristic)
  const rxTitle = /\b([A-Z][a-zA-Z'&\-]+(?:\s+[A-Z][a-zA-Z'&\-]+){1,4})\b/g;
  while ((m = rxTitle.exec(raw))) {
    const n = normalizeName(m[1]);
    if (!n) continue;
    const tokens = n.split(' ');
    if (tokens.length >= 2 && tokens.every(t => !STOP_WORDS.has(t))) out.add(n);
  }
  return Array.from(out).slice(0, 5);
}

function extractFromHashtags(tags: string[] | undefined): { names: string[]; locs: string[] } {
  const names = new Set<string>();
  const locs = new Set<string>();
  if (!tags) return { names: [], locs: [] };
  for (const t of tags) {
    const base = normalizeName(t.replace(/^#/, ''));
    if (!base) continue;
    if (base.length <= 2) continue;
    if (/(cdmx|mexico|méxico|roma|condesa|polanco|napoles|nápoles|guadalajara|monterrey|nyc|la|miami|chicago|london|paris|madrid|barcelona)\b/.test(base)) {
      locs.add(base);
      continue;
    }
    if (!STOP_WORDS.has(base)) {
      names.add(base);
    }
  }
  return { names: Array.from(names).slice(0, 5), locs: Array.from(locs).slice(0, 5) };
}

function buildQueries(candidates: string[], cityHint: string, locTags: string[]): string[] {
  const queries: string[] = [];
  const city = (cityHint || '').trim();
  for (const c of candidates) {
    const name = c.trim();
    if (!name) continue;
    queries.push(name);
    if (city) queries.push(`${name} ${city}`);
    for (const loc of locTags) {
      if (loc && loc !== city) queries.push(`${name} ${loc}`);
    }
    if (queries.length >= 8) break;
  }
  // dedupe, keep first 5 strongest
  return Array.from(new Set(queries)).slice(0, 5);
}

function scoreCandidate(place: ServerPlace, queries: string[], names: string[]): { score: number; reasons: string[] } {
  let score = 0;
  const reasons: string[] = [];
  const placeNameNorm = normalizeName(place.name);

  // restaurant type check
  const isRest = (place.types || []).some(t => /restaurant|food|cafe|bakery|bar|meal_/.test(t));
  if (isRest) { score += 0.25; reasons.push('type:restaurant'); }

  // exact/near-exact name match against extracted names
  for (const n of names) {
    const nn = normalizeName(n);
    if (!nn) continue;
    if (placeNameNorm === nn) { score += 0.6; reasons.push(`name:exact:${n}`); break; }
    if (placeNameNorm.includes(nn)) { score += 0.45; reasons.push(`name:includes:${n}`); break; }
    // token overlap fraction
    const ptoks = new Set(placeNameNorm.split(' '));
    const ntoks = nn.split(' ').filter(Boolean);
    const overlap = ntoks.filter(t => ptoks.has(t)).length / Math.max(1, ntoks.length);
    if (overlap >= 0.6) { score += 0.4; reasons.push(`name:overlap:${Math.round(overlap*100)}%`); break; }
  }

  // query token inclusion bonus
  const qTokens = new Set(queries.join(' ').toLowerCase().split(/\s+/));
  const matched = placeNameNorm.split(' ').filter(w => qTokens.has(w) && w.length > 3).length;
  if (matched) { score += Math.min(0.25, 0.05 * matched); reasons.push('q:name-tokens'); }

  // popularity nudge
  if ((place.user_ratings_total || 0) >= 50) { score += 0.05; reasons.push('popular'); }

  return { score: Math.min(1, score), reasons };
}

export async function findRestaurant(input: FindInput, opts?: { signal?: AbortSignal }): Promise<FindOutput> {
  // 1) Gather metadata and text
  let meta: any = {};
  if (input.mediaUrl && !input.file) {
    try {
      if (typeof window !== 'undefined') (window as any).__telemetry = { step: 'oembed:start' };
      meta = await fetchBasicMeta(input.mediaUrl, { signal: opts?.signal });
      if (typeof window !== 'undefined') (window as any).__telemetry = { step: 'oembed:done', meta_title: meta?.title };
    } catch {
      if (typeof window !== 'undefined') (window as any).__telemetry = { step: 'oembed:error' };
    }
  }
  const text = [meta?.title, meta?.description, input.caption, ...(input.hashtags || [])].filter(Boolean).join(' ');
  const tagSet = extractFromHashtags(input.hashtags || []);
  const nameCandidates = Array.from(new Set([
    ...extractCandidatesFromText(text),
    ...tagSet.names,
  ])).slice(0, 5);

  // 2) Build queries
  let queries = buildQueries(nameCandidates, input.cityHint || '', tagSet.locs);
  if (queries.length === 0) return [];

  // 3) Optional geobias from cityHint
  let bias: { lat?: number; lng?: number; radius?: number } = {};
  if (input.cityHint && /[a-zA-Z]/.test(input.cityHint)) {
    try {
      const loc = await geocodeAddress(input.cityHint);
      if (loc) bias = { lat: loc.lat, lng: loc.lng, radius: 20000 };
    } catch {}
  } else if (input.cityHint && /-?\d+\.\d+,-?\d+\.\d+(?:,\d+)?/.test(input.cityHint)) {
    try {
      const [lat, lng, r] = input.cityHint.split(',').map(Number);
      bias = { lat, lng, radius: (r || 20) * 1000 };
    } catch {}
  }

  // 4) Search Places
  const all: ServerPlace[] = [];
  for (const q of queries) {
    try {
      const res = await searchPlacesServer(q, bias);
      all.push(...res);
    } catch {}
  }
  if (all.length === 0) {
    // Try gated transcription fallback, then rebuild candidates and queries once
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const fnBase = (process.env.REACT_APP_FUNCTIONS_BASE || '').replace(/\/$/, '');
      const resp = await fetch(new URL(`${fnBase}/transcribe`, origin).toString(), {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ url: input.mediaUrl, max_seconds: 90 }),
      });
      if (resp.ok) {
        const j = await resp.json();
        const transcript = (j?.text || '').toString();
        if (transcript) {
          const augmented = [text, transcript].join(' \n ');
          const names2 = Array.from(new Set([
            ...nameCandidates,
            ...extractCandidatesFromText(augmented),
          ])).slice(0, 7);
          const tags2 = tagSet.locs;
          queries = buildQueries(names2, input.cityHint || '', tags2);
          if (queries.length) {
            for (const q of queries) {
              try { const res2 = await searchPlacesServer(q, bias); all.push(...res2); } catch {}
            }
          }
        }
      }
    } catch {}
    if (all.length === 0) return [];
  }

  // 5) Score and pick top
  const byId = new Map<string, ServerPlace>();
  for (const p of all) {
    const prev = byId.get(p.place_id);
    if (!prev || (p.user_ratings_total || 0) > (prev.user_ratings_total || 0)) byId.set(p.place_id, p);
  }
  const uniq = Array.from(byId.values());
  const scored = uniq.map(p => {
    const s = scoreCandidate(p, queries, nameCandidates);
    return { p, score: s.score, reasons: s.reasons };
  }).sort((a, b) => b.score - a.score);

  const top = scored[0];
  if (!top || top.score < 0.7) return [];

  // 6) Enrich ALL results with details (especially top 3)
  const enriched = await Promise.allSettled(
    scored.slice(0, 3).map(async (s) => {
      let details: any = null;
      try { 
        details = await getPlaceDetailsServer(s.p.place_id);
      } catch {}
      
      // Map cuisine from place types
      const mapCuisine = (types: string[]): string | undefined => {
        if (!Array.isArray(types)) return undefined;
        const t = types.join(' ').toLowerCase();
        const checks: Array<[RegExp, string]> = [
          [/ramen|noodle/, 'Ramen'],
          [/japanese/, 'Japanese'],
          [/mexican|taco|taqueria/, 'Mexican'],
          [/italian|pizza|pizzeria/, 'Italian'],
          [/thai/, 'Thai'],
          [/korean/, 'Korean'],
          [/chinese/, 'Chinese'],
          [/indian|curry/, 'Indian'],
          [/mediterranean|greek/, 'Mediterranean'],
          [/seafood/, 'Seafood'],
          [/burger/, 'Burgers'],
          [/cafe|coffee/, 'Cafe'],
          [/bakery/, 'Bakery'],
        ];
        for (const [re, label] of checks) if (re.test(t)) return label;
        return undefined;
      };
      
      return {
        placeId: s.p.place_id,
        name: s.p.name,
        address: s.p.formatted_address,
        confidence: s.score,
        reasons: s.reasons,
        rating: details?.rating,
        website: details?.website,
        url: details?.url,
        phone: details?.formatted_phone_number,
        cuisine: mapCuisine(details?.types || s.p.types || []),
        openNow: details?.opening_hours?.open_now,
        hours: details?.opening_hours?.weekday_text,
        priceLevel: details?.price_level,
      };
    })
  );

  return enriched
    .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled')
    .map(r => r.value);
}


